/*
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：稼働実績分析系のAPI
 *
 * 作成者：2021/11/30 ノア）黄
 * 最終修正者：2021/11/30 ノア）黄
 *
 */
import { apiClient } from '@/store/apis/apiClient'
import config from '@/config/config'
import {
  ManagementTargetAnalysisData,
  FileDownloadResponse,
  OpPerfMvmtParam,
  OpPerfAreaParam,
  OpPerfAreaPrefectureParam,
  OpPerfAreaData,
  OpPerfAreaPrefectureData,
  OpPerfAreaManageParam,
  ManagementObjectAnalysisParam
} from '@/models'

/**
 * 都道府県別分析移動履歴一覧取得
 *
 * @param {ManagementObjectAnalysisParam} reqParams パラメータ
 * @returns {Promise<ManagementTargetAnalysisData>} 管理対象分析Model
 */
export async function getManagementObjectAnalysisData(
  reqParams: ManagementObjectAnalysisParam
): Promise<ManagementTargetAnalysisData> {
  const res = await apiClient.post(config.api.managementObjectAnalysisData, reqParams)

  return res.data.data as ManagementTargetAnalysisData
}

/**
 * 移動履歴管理表指示日時管理CSVダウンロード
 *
 * @param {OpPerfMvmtParam} reqParams パラメータ
 * @returns {Promise<FileDownloadResponse>} ダウンロードファイル
 */
export async function opPerfIndicateDateCSVDownload(reqParams: OpPerfMvmtParam): Promise<FileDownloadResponse> {
  const res = await apiClient.post(config.api.opPerfIndicateDateCSVDownload, reqParams, { responseType: 'blob' })

  return res as unknown as FileDownloadResponse
}

/**
 * 移動履歴管理表指示日時管理PDFダウンロード
 *
 * @param {OpPerfMvmtParam} reqParams パラメータ
 * @returns {Promise<FileDownloadResponse>} ダウンロードファイル
 */
export async function opPerfIndicateDatePDFDownload(reqParams: OpPerfMvmtParam): Promise<FileDownloadResponse> {
  const res = await apiClient.post(config.api.opPerfIndicateDatePDFDownload, reqParams, { responseType: 'blob' })

  return res as unknown as FileDownloadResponse
}

/**
 * 移動履歴管理表CSVダウンロード
 *
 * @param {OpPerfMvmtParam} reqParams パラメータ
 * @returns {Promise<FileDownloadResponse>} ダウンロードファイル
 */
export async function opPerfMvmtCSVDownload(reqParams: OpPerfMvmtParam): Promise<FileDownloadResponse> {
  const res = await apiClient.post(config.api.opPerfMvmtCSVDownload, reqParams, { responseType: 'blob' })

  return res as unknown as FileDownloadResponse
}

/**
 * 移動履歴管理表PDFダウンロード
 *
 * @param {OpPerfMvmtParam} reqParams パラメータ
 * @returns {Promise<FileDownloadResponse>} ダウンロードファイル
 */
export async function opPerfMvmtPDFDownload(reqParams: OpPerfMvmtParam): Promise<FileDownloadResponse> {
  const res = await apiClient.post(config.api.opPerfMvmtPDFDownload, reqParams, { responseType: 'blob' })

  return res as unknown as FileDownloadResponse
}

/**
 * 営業所別分析CSVダウンロード
 *
 * @param {OpPerfMvmtParam} reqParams パラメータ
 * @returns {Promise<FileDownloadResponse>} ダウンロードファイル
 */
export async function opPerfOfcCSVDownload(reqParams: OpPerfMvmtParam): Promise<FileDownloadResponse> {
  const res = await apiClient.post(config.api.opPerfOfcCSVDownload, reqParams, { responseType: 'blob' })

  return res as unknown as FileDownloadResponse
}

/**
 * 営業所別分析PDFダウンロード
 *
 * @param {OpPerfMvmtParam} reqParams パラメータ
 * @returns {Promise<FileDownloadResponse>} ダウンロードファイル
 */
export async function opPerfOfcPDFDownload(reqParams: OpPerfMvmtParam): Promise<FileDownloadResponse> {
  const res = await apiClient.post(config.api.opPerfOfcPDFDownload, reqParams, { responseType: 'blob' })

  return res as unknown as FileDownloadResponse
}

/**
 * エリア別分析結果取得
 *
 * @async
 * @param {OpPerfAreaParam} reqParams パラメータ
 * @returns {Promise<OpPerfAreaData>} エリア別分析結果
 */
export async function getOpPerfArea(reqParams: OpPerfAreaParam): Promise<OpPerfAreaData> {
  const res = await apiClient.post(config.api.opPerfArea, reqParams)

  return res.data.data as OpPerfAreaData
}

/**
 * 都道府県別分析結果取得
 *
 * @async
 * @param {OpPerfAreaPrefectureParam} reqParams パラメータ
 * @returns {Promise<OpPerfAreaPrefectureData>} 都道府県別分析結果
 */
export async function getOpPerfAreaPrefecture(reqParams: OpPerfAreaPrefectureParam): Promise<OpPerfAreaPrefectureData> {
  const res = await apiClient.post(config.api.opPerfAreaPrefecture, reqParams)

  return res.data.data as OpPerfAreaPrefectureData
}

/**
 * エリア別分析PDFダウンロード
 *
 * @param {OpPerfAreaParam} reqParams パラメータ
 * @returns {Promise<FileDownloadResponse>} ダウンロードファイル
 */
export async function opPerfAreaPDFDownload(reqParams: OpPerfAreaParam): Promise<FileDownloadResponse> {
  const res = await apiClient.post(config.api.opPerfAreaPDFDownload, reqParams, { responseType: 'blob' })

  return res as unknown as FileDownloadResponse
}

/**
 * 都道府県別分析PDFダウンロード
 *
 * @param {OpPerfAreaPrefectureParam} reqParams パラメータ
 * @returns {Promise<FileDownloadResponse>} ダウンロードファイル
 */
export async function opPerfPrefecturePDFDownload(reqParams: OpPerfAreaPrefectureParam): Promise<FileDownloadResponse> {
  const res = await apiClient.post(config.api.opPerfPrefecturePDFDownload, reqParams, { responseType: 'blob' })

  return res as unknown as FileDownloadResponse
}

/**
 * 都道府県別分析CSVダウンロード
 *
 * @param {OpPerfAreaParam} reqParams パラメータ
 * @returns {Promise<FileDownloadResponse>} ダウンロードファイル
 */
export async function opPerfPrefectureCSVDownload(reqParams: OpPerfAreaParam): Promise<FileDownloadResponse> {
  const res = await apiClient.get(config.api.opPerfPrefectureCSVDownload, { params: reqParams, responseType: 'blob' })

  return res as unknown as FileDownloadResponse
}

/**
 * エリア別分析移動履歴CSVダウンロード
 *
 * @param {OpPerfAreaManageParam} reqParams パラメータ
 * @returns {Promise<FileDownloadResponse>} ダウンロードファイル
 */
export async function opPerfAreaManageCSVDownload(reqParams: OpPerfAreaManageParam): Promise<FileDownloadResponse> {
  const res = await apiClient.post(config.api.opPerfAreaManageCSVDownload, reqParams, { responseType: 'blob' })

  return res as unknown as FileDownloadResponse
}

/**
 * エリア別分析移動履歴PDFダウンロード
 *
 * @param {OpPerfAreaManageParam} reqParams パラメータ
 * @returns {Promise<FileDownloadResponse>} ダウンロードファイル
 */
export async function opPerfAreaManagePDFDownload(reqParams: OpPerfAreaManageParam): Promise<FileDownloadResponse> {
  const res = await apiClient.post(config.api.opPerfAreaManagePDFDownload, reqParams, { responseType: 'blob' })

  return res as unknown as FileDownloadResponse
}
